import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

const CATEGORIES = [
  'Frontend Engineering',
  'Backend Systems',
  'Database & Data',
  'Infrastructure (DevOps)',
  'Mobile Development',
  'AI & Machine Learning',
  'Testing & QA',
  'Security & Auth',
  'Distributed Systems',
  'Documentation',
];

const SHORT_NAMES: Record<string, string> = {
  'Frontend Engineering': 'Frontend',
  'Backend Systems': 'Backend',
  'Database & Data': 'Database',
  'Infrastructure (DevOps)': 'DevOps',
  'Mobile Development': 'Mobile',
  'AI & Machine Learning': 'AI/ML',
  'Testing & QA': 'Testing',
  'Security & Auth': 'Security',
  'Distributed Systems': 'Distributed',
  'Documentation': 'Docs',
};

const CENTER_X = 180;
const CENTER_Y = 180;
const MAX_RADIUS = 130;
const LABEL_RADIUS = 155;
const NUM_LEVELS = 5;
const SVG_SIZE = 360;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function buildPolygon(values: number[], maxVal: number): string {
  const step = 360 / values.length;
  const pts = values.map((v, i) => {
    const r = (Math.max(v, 0) / maxVal) * MAX_RADIUS;
    const p = polarToCartesian(CENTER_X, CENTER_Y, r, i * step);
    return `${p.x},${p.y}`;
  });
  return pts.join(' ');
}

function buildGridPolygon(level: number): string {
  const r = (level / NUM_LEVELS) * MAX_RADIUS;
  const step = 360 / CATEGORIES.length;
  return CATEGORIES.map((_, i) => {
    const p = polarToCartesian(CENTER_X, CENTER_Y, r, i * step);
    return `${p.x},${p.y}`;
  }).join(' ');
}

const useStyles = makeStyles(theme => ({
  svg: {
    width: '100%',
    maxWidth: SVG_SIZE,
    display: 'block',
    margin: '0 auto',
  },
  gridPolygon: {
    fill: 'none',
    stroke: theme.palette.divider,
    strokeWidth: 1,
  },
  radialLine: {
    stroke: theme.palette.divider,
    strokeWidth: 1,
  },
  dataPolygon: {
    fill: 'rgba(63, 81, 181, 0.18)',
    stroke: '#3f51b5',
    strokeWidth: 2,
  },
  comparePolygon: {
    fill: 'rgba(233, 30, 99, 0.12)',
    stroke: '#e91e63',
    strokeWidth: 2,
  },
  dataPoint: {
    fill: '#3f51b5',
  },
  comparePoint: {
    fill: '#e91e63',
  },
  label: {
    fontSize: 10,
    fontWeight: 600,
    fill: theme.palette.text.primary,
    dominantBaseline: 'middle',
  },
}));

export interface SpiderChartData {
  /** Map of category name → proficiency value (0-10 scale) */
  categoryAverages: Array<{ category: string; averageProficiency: number }>;
}

interface SpiderChartProps {
  primary: SpiderChartData;
  /** Optional second dataset shown as overlay (e.g. platform average or second developer) */
  compare?: SpiderChartData;
  maxValue?: number;
}

export const SpiderChart = ({ primary, compare, maxValue = 10 }: SpiderChartProps) => {
  const classes = useStyles();

  const primaryMap = new Map(primary.categoryAverages.map(c => [c.category, c.averageProficiency]));
  const compareMap = compare
    ? new Map(compare.categoryAverages.map(c => [c.category, c.averageProficiency]))
    : null;

  const primaryValues = CATEGORIES.map(cat => Math.max(primaryMap.get(cat) ?? 0, 1));
  const compareValues = compareMap ? CATEGORIES.map(cat => Math.max(compareMap.get(cat) ?? 0, 1)) : null;

  const step = 360 / CATEGORIES.length;

  return (
    <svg
      viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      className={classes.svg}
      aria-label="Skill radar chart"
    >
      {/* Grid */}
      {Array.from({ length: NUM_LEVELS }, (_, i) => i + 1).map(level => (
        <polygon
          key={level}
          points={buildGridPolygon(level)}
          className={classes.gridPolygon}
        />
      ))}

      {/* Radial lines */}
      {CATEGORIES.map((_, i) => {
        const outer = polarToCartesian(CENTER_X, CENTER_Y, MAX_RADIUS, i * step);
        return (
          <line
            key={i}
            x1={CENTER_X}
            y1={CENTER_Y}
            x2={outer.x}
            y2={outer.y}
            className={classes.radialLine}
          />
        );
      })}

      {/* Compare overlay */}
      {compareValues && (
        <React.Fragment>
          <polygon
            points={buildPolygon(compareValues, maxValue)}
            className={classes.comparePolygon}
          />
          {compareValues.map((v, i) => {
            const r = (Math.max(v, 0) / maxValue) * MAX_RADIUS;
            const p = polarToCartesian(CENTER_X, CENTER_Y, r, i * step);
            return <circle key={i} cx={p.x} cy={p.y} r={3} className={classes.comparePoint} />;
          })}
        </React.Fragment>
      )}

      {/* Primary data */}
      <polygon
        points={buildPolygon(primaryValues, maxValue)}
        className={classes.dataPolygon}
      />
      {primaryValues.map((v, i) => {
        const r = (Math.max(v, 0) / maxValue) * MAX_RADIUS;
        const p = polarToCartesian(CENTER_X, CENTER_Y, r, i * step);
        return <circle key={i} cx={p.x} cy={p.y} r={4} className={classes.dataPoint} />;
      })}

      {/* Category labels */}
      {CATEGORIES.map((cat, i) => {
        const pos = polarToCartesian(CENTER_X, CENTER_Y, LABEL_RADIUS, i * step);
        let anchor: 'inherit' | 'end' | 'start' | 'middle' = 'middle';
        if (pos.x < CENTER_X - 10) anchor = 'end';
        else if (pos.x > CENTER_X + 10) anchor = 'start';
        return (
          <text
            key={cat}
            x={pos.x}
            y={pos.y}
            textAnchor={anchor}
            className={classes.label}
          >
            {SHORT_NAMES[cat] ?? cat}
          </text>
        );
      })}
    </svg>
  );
};
